import { Controller } from "@hotwired/stimulus"
import flatpickr from "flatpickr"
import {
  flatpickrLocale,
  protectFlatpickrTranslation
} from "../helpers/flatpickr_locale"

export default class FlatpickrController extends Controller {
  static values = {
    type: String
  }

  connect() {
    this.handleLanguageChange = this.languageChange.bind(this)
    document.addEventListener("itoons:language-change", this.handleLanguageChange)

    if (bootstrap.Modal.prototype._enforceFocus) {
      bootstrap.Modal.prototype._enforceFocus = function () { }
    }

    const lockTimeInputs = (instance) => {
      const timeInputs = [instance.hourElement, instance.minuteElement, instance.secondElement]
        .filter(Boolean)

      for (const input of timeInputs) {
        input.setAttribute("readonly", "readonly")
        input.setAttribute("inputmode", "none")
        input.style.caretColor = "transparent"
      }
    }

    const showConfirmButton = this.typeValue === "datetime"
    let clearButton

    const syncClearButton = (selectedDates) => {
      clearButton?.classList.toggle("d-none", selectedDates.length === 0)
    }

    let config = {
      locale: flatpickrLocale(),
      disableMobile: true,
      allowInput: false,
      appendTo: document.body,

      onOpen: (selectedDates, dateStr, instance) => {
        if (instance.config.enableTime) {
          lockTimeInputs(instance)
        }
      },

      onClose: () => {
      },

      onReady: function (selectedDates, dateStr, instance) {
        protectFlatpickrTranslation(instance)
        if (instance.input.closest(".modal")) {
          instance.calendarContainer.classList.add("flatpickr-modal-calendar")
        }

        const calendarClass = instance.input.dataset.flatpickrCalendarClass
        if (calendarClass) {
          instance.calendarContainer.classList.add(calendarClass)
        }

        if (showConfirmButton) {
          const confirmButtonWrapper = document.createElement("div")
          confirmButtonWrapper.className = "flatpickr-confirm-button-wrapper d-flex justify-content-end gap-2 p-2"

          clearButton = document.createElement("button")
          clearButton.type = "button"
          clearButton.className = "btn py-2 px-3 rounded-2 bg-tomato text-black p-font-bold fs-14 flatpickr-clear-button"
          clearButton.textContent = "Clear"
          clearButton.addEventListener("click", () => {
            instance.clear()
            syncClearButton(instance.selectedDates)
          })

          const confirmButton = document.createElement("button")
          confirmButton.type = "button"
          confirmButton.className = "btn py-2 px-3 rounded-2 bg-info text-black p-font-bold fs-14 flatpickr-confirm-button"
          confirmButton.textContent = "Done"
          confirmButton.addEventListener("click", () => instance.close())

          confirmButtonWrapper.append(clearButton, confirmButton)
          instance.calendarContainer.appendChild(confirmButtonWrapper)
          syncClearButton(selectedDates)
        }

        if (!instance.config.enableTime) return

        lockTimeInputs(instance)

        const inputs = [instance.hourElement, instance.minuteElement].filter(Boolean)

        for (const [index, input] of inputs.entries()) {
          input.addEventListener("input", function () {
            input.value = input.value.replaceAll(/\D/g, "").slice(0, 2)
          })

          input.addEventListener("blur", function () {
            if (input.value === "") return

            let max = index === 0 ? 23 : 59
            let val = Number.parseInt(input.value, 10)

            val = Math.min(max, Math.max(0, val))

            input.value = val.toString().padStart(2, "0")
          })
        }
      },

      onValueUpdate: (selectedDates) => syncClearButton(selectedDates)
    }

    if (this.typeValue === "time") {
      Object.assign(config, {
        enableTime: true,
        noCalendar: true,
        dateFormat: "H:i",
        time_24hr: true,
        altInput: true,
        altFormat: "H:i",
        minuteIncrement: 1
      })
    } else if (this.typeValue === "customtime") {
      Object.assign(config, {
        enableTime: true,
        noCalendar: true,
        dateFormat: "h:i K",
        time_24hr: false,
        altInput: true,
        altFormat: "h:i K",
        minuteIncrement: 1
      })
    } else if (this.typeValue === "datetime") {
      Object.assign(config, {
        enableTime: true,
        altFormat: "Y-m-d    h:i K",
        dateFormat: "Y-m-d h:i K",
        altInput: true,
        time_24hr: false
      })
    } else {
      Object.assign(config, {
        altFormat: "m-d-Y",
        dateFormat: "Y-m-d",
        altInput: true,
      })
    }

    this.fp = flatpickr(this.element, config)
    this.preventManualDeletion = (event) => {
      if (event.key !== "Backspace" && event.key !== "Delete") return

      event.preventDefault()
      event.stopImmediatePropagation()
    }
    this.fp._input.addEventListener("keydown", this.preventManualDeletion, true)
  }

  disconnect() {
    document.removeEventListener("itoons:language-change", this.handleLanguageChange)
    document.body.classList.remove("flatpickr-open")
    this.fp?._input?.removeEventListener("keydown", this.preventManualDeletion, true)
    this.fp?.destroy()
  }

  languageChange(event) {
    this.fp?.set("locale", flatpickrLocale(event.detail?.language))
  }

  clear() {
    this.fp?.clear()
  }
}
